// ============================================================================
// sale-format.js — ตัวช่วยจัดรูปแบบ/ลิงก์ ที่หน้าแรกกับหน้าห้องใช้ร่วมกัน
// ------------------------------------------------------------
// ★ ทำไมต้องรวมไว้ที่เดียว
//   เดิม STATUS_COLOR / money / ตัวแปลง tel: / lineUrl ก๊อปกระจายอยู่หลายไฟล์
//   แก้ที่หนึ่งแล้วอีกที่ไม่ตาม (เช่น HomeCta ใช้ลิงก์ LINE คนละแบบกับหน้าห้อง
//   LINE ID ส่วนตัวเลยเปิดไม่ได้เฉพาะหน้าแรก)
//
// ★ ทุกค่าที่มาจากผู้ดูแล/ผู้ประกาศแล้วจะไปลง href ต้องผ่าน safeUrl() เสมอ
//   React 18 แค่เตือนเวลาเจอ href="javascript:..." แต่ยังรันเมื่อกด
// ============================================================================

// ใช้เฉพาะชื่อสีที่มีอยู่จริงใน homepage.css (hm-badge--*)
// ชื่อที่ไม่มีจะได้ป้ายไม่มีสีพื้น มองไม่ออกว่าเป็นป้าย
export const STATUS_COLOR = {
    "ว่าง": "green",
    "จอง": "yellow",
    "ขายแล้ว": "light",
    "ให้เช่าแล้ว": "light",
    "ยกเลิก": "light",
};

// ตัวเลขเงินแบบไทย ไม่มีทศนิยม (ราคาอสังหาไม่ต้องการเศษสตางค์บนหน้าเว็บ)
export const money = (v) => Number(v || 0).toLocaleString("th-TH", { maximumFractionDigits: 0 });

// ค่าที่ถือว่า "มีข้อมูล" — 0 นับเป็นไม่มี (DB เก็บ default 0 ไว้ตอนผู้ประกาศไม่ได้กรอก)
export const hasValue = (v) => v !== null && v !== undefined && v !== "" && v !== 0;

// ------------------------------------------------------------ ลิงก์ปลอดภัย
// ★ รับเฉพาะ http(s) / tel / mailto / line — ที่เหลือ (javascript:, data:, vbscript: ...) คืน ""
//   ผู้เรียกต้องเช็คค่าว่างแล้วไม่แสดงลิงก์ ไม่ใช่ใส่ href ว่าง
const SAFE_SCHEME = /^(https?:\/\/|tel:|mailto:|line:\/\/)/i;

export const safeUrl = (value) => {
    const v = String(value ?? "").trim();
    if (!v) return "";
    // อักขระควบคุม/ช่องว่างกลาง scheme (เช่น "java\nscript:") เบราว์เซอร์ตัดทิ้งแล้วรันได้ — ปฏิเสธไปเลย
    // eslint-disable-next-line no-control-regex
    if (/[ -]/.test(v)) return "";
    return SAFE_SCHEME.test(v) ? v : "";
};

// เว็บไซต์ที่ผู้ดูแลกรอกมักไม่มี http นำหน้า (เช่น "www.abc.co.th")
// ★ ถ้าไม่เติมให้ เบราว์เซอร์จะตีเป็นลิงก์ภายในเว็บเรา (/www.abc.co.th) กดแล้วเจอหน้า 404
export const websiteUrl = (value) => {
    const v = String(value ?? "").trim();
    if (!v) return "";
    if (/^[a-z][a-z0-9+.-]*:/i.test(v)) return safeUrl(v); // มี scheme มาแล้ว — ปล่อยให้ safeUrl ตัดสิน
    return safeUrl(`https://${v.replace(/^\/+/, "")}`);
};

// Facebook : กรอกได้ทั้งลิงก์เต็ม / โดเมน (facebook.com/xxx) / ชื่อเพจเฉย ๆ
export const facebookUrl = (value) => {
    const v = String(value ?? "").trim();
    if (!v) return "";
    if (/^[a-z][a-z0-9+.-]*:/i.test(v) || /^(www\.|m\.)?(facebook|fb)\.(com|me)\//i.test(v)) return websiteUrl(v);
    return safeUrl(`https://www.facebook.com/${encodeURIComponent(v.replace(/^@/, ""))}`);
};

// ลิงก์เปิดแชท LINE จากค่าที่ผู้ดูแลกรอก
// ★ กรอกได้หลายแบบ : ลิงก์เต็ม (https://line.me/...) / Official Account (@xxx) / LINE ID ส่วนตัว
//   Official Account ใช้ /R/ti/p/@id · ID ส่วนตัวต้องมี ~ นำหน้า (/ti/p/~id) ไม่งั้นเปิดไม่เจอ
export const lineUrl = (value) => {
    const v = String(value ?? "").trim();
    if (!v) return "";
    if (/^[a-z][a-z0-9+.-]*:/i.test(v)) return safeUrl(v);
    if (/^(www\.)?line\.(me|ee)\//i.test(v)) return websiteUrl(v);
    if (v.startsWith("@")) return `https://line.me/R/ti/p/${encodeURIComponent(v)}`;
    return `https://line.me/ti/p/~${encodeURIComponent(v)}`;
};

// ------------------------------------------------------------ เบอร์โทร
// ลิงก์โทรออก — เก็บแค่ตัวเลขกับ + (ขีด/เว้นวรรคในเบอร์ทำให้บางเครื่องโทรไม่ติด)
export const telUrl = (value) => {
    const digits = String(value ?? "").replace(/[^\d+]/g, "");
    return /\d/.test(digits) ? `tel:${digits}` : "";
};

// เบอร์ที่ผู้ใช้กรอกในฟอร์ม → ตัวเลขล้วนแบบไทย
// ★ ต้องตรงกับ normalizePhone ฝั่ง backend (modules/sale-public/sale-public.service.js)
//   66 นำหน้า (ยาวเกิน 9 หลัก) = รหัสประเทศ → เปลี่ยนเป็น 0 และตัด 0 ที่ซ้อนหลัง 66 ทิ้ง
// ★ normalize ก่อนตัดความยาวเสมอ — เดิมตัด 10 หลักก่อน "+66812345678" เลยเหลือ "6681234567"
//   แล้ว backend แปลงเป็น "081234567" ซึ่งผ่าน pattern เบอร์หาย 1 หลักโดยไม่มีใครรู้
export const normalizePhone = (value) => {
    let digits = String(value ?? "").replace(/\D/g, "");
    if (digits.length > 9 && digits.startsWith("66")) {
        digits = "0" + digits.slice(2).replace(/^0+/, "");
    }
    return digits;
};

// รูปแบบเดียวกับ PHONE_PATTERN ฝั่ง backend : 0 ตามด้วยตัวเลข 8-9 หลัก
export const PHONE_PATTERN = /^0\d{8,9}$/;

// ------------------------------------------------------------ หน่วย/คำนำหน้า
// ตัดคำนำหน้าที่ผู้ประกาศพิมพ์มาเอง (เช่น "ชั้น 5", "อาคาร 62") ก่อนหน้าจอเติมคำนั้นให้อีกรอบ
// ★ เดิมได้ "ชั้น 5 ชั้น" / "อาคาร 62 อาคาร" — ฟอร์มหลังบ้านรับข้อความอิสระ บางห้องมีคำนำหน้า บางห้องไม่มี
//   ตัดแล้วว่าง (พิมพ์มาแค่ "ชั้น") = คืนค่าเดิม ไม่ให้ช่องหายไปเฉย ๆ
export const stripPrefixWord = (value, word) => {
    const v = String(value ?? "").trim();
    if (!word || !v.startsWith(word)) return v;
    return v.slice(word.length).trim() || v;
};

// ------------------------------------------------------------ วันที่
// วันที่แบบไทย dd/mm/yyyy (พ.ศ.) — รูปแบบเดียวกับ DatePicker ของหน้าเว็บและหน้า admin (format_date)
// ★ เดิมหน้าห้องโชว์ค่าดิบ String(...).slice(0, 10) เช่น "2026-09-15"
//   ค่าที่มี timezone (ISO จาก API เช่น ...T17:00:00.000Z) แปลงเป็นวันตามเวลาไทยก่อน
//   ไม่งั้นวันที่เก็บเป็นเที่ยงคืนเวลาไทยจะเลื่อนเป็นเมื่อวาน · ค่า YYYY-MM-DD เฉย ๆ ใช้ตัวเลขตรง ๆ ไม่ผ่าน Date
export const thaiDate = (value) => {
    if (!value) return "";
    const text = String(value).trim();
    if (/T.*(Z|[+-]\d{2}:?\d{2})$/i.test(text)) {
        const d = new Date(text);
        if (!Number.isNaN(d.getTime())) {
            const parts = new Intl.DateTimeFormat("en-GB", {
                timeZone: "Asia/Bangkok",
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
            }).formatToParts(d);
            const get = (type) => parts.find((p) => p.type === type)?.value;
            return `${get("day")}/${get("month")}/${Number(get("year")) + 543}`;
        }
    }
    const m = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${Number(m[1]) + 543}` : text;
};

// วันนี้ในรูป YYYY-MM-DD ตามเวลาเครื่องผู้ใช้ (ไม่ใช่ UTC — ใช้ toISOString แล้วช่วงเช้ามืดจะได้เมื่อวาน)
export const todayIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
